/**
 * Common - Wishlist management.
 */

// Imports
import GlobalVars from '../common/GlobalVars';
import AjaxURL from '../common/AjaxURL';
import {
  isEmpty,
  type,
} from './Utils';
import {
  triggerWishlistItemAddedEvent,
  triggerWishlistItemRemovedEvent,
} from './Events';

const jsAddToWishlistClass = 'js-addToWishlist';

const loginUrlAttr = 'login-url';

/**
 * List of SKUs for all the products in wishlist.
 * @type {Array}
 */
let skusList = [];

function getItemsCount() {
  return skusList.length;
}

function hasItems() {
  return (skusList.length > 0);
}

function getItemIndex(asSKU) {
  let index = skusList.length;

  while (index >= 0) {
    index -= 1;

    if (skusList[index] === asSKU) {
      break;
    }
  }

  return index;
}

function updateSkusList(aaSkusList) {
  skusList = aaSkusList || [];
}

/**
 * Sends a request to the server to add one or more items to the wishlist.
 * @param {Array}    aaSkusList            - List of SKUs.
 * @param {Array}    aaProductList         - List of product objects.
 * @param {String}   aaProductList[].sku   - SKU for the product.
 * @param {String}   aaProductList[].title - Title for the product.
 * @param {String}   aaProductList[].size  - Size for the product.
 * @param {String}   aaProductList[].price - Price for the product.
 * @param {Function} [aoCallback] - Callback function with one argument:
 * - boolean: whether the request succeeded or not
 */
function addToWishList(aaSkusList, aaProductList, aoCallback) {
  $.ajax({
    url: AjaxURL.addToWishList,
    data: {
      task: 'addToWishList',
      c: GlobalVars.C,
      l: GlobalVars.L,
      skus: aaSkusList,
    },
    dataType: 'json',
    type: 'POST',
    traditional: true,
  }).done((aoData) => {
    const success = aoData && aoData.success;

    if (typeof aoCallback === 'function') {
      // Call the given callback
      aoCallback(success, aaProductList);
    }

    // Trigger the cart update event
    // triggerWishlistItemAddedEvent(aaProductList);
    triggerWishlistItemAddedEvent({
      name: OCMS.productpage.productsData.current.length > 0 ? OCMS.productpage.productsData.current[0].ShortTitle : 'None',
      id: OCMS.productpage.sku,
    });
  }).fail(() => {
    if (typeof aoCallback === 'function') {
      // Call the given callback
      aoCallback(false, aaProductList);
    }
  });
}

/**
 * Sends a request to the server to remvoe an item from the wishlist.
 * @param {String} asSKU - SKU
 * @param {Function} [aoCallback] - Callback function with one argument:
 * - boolean: whether the request succeeded or not
 */
function removeFromWishList(asSKU, aoCallback) {
  $.ajax({
    url: AjaxURL.removeFromWishList,
    data: {
      task: 'deleteFavori',
      c: GlobalVars.C,
      l: GlobalVars.L,
      sku: asSKU,
    },
    dataType: 'json',
    type: 'POST',
    traditional: true,
  }).done((aoData) => {
    const success = aoData && aoData.success;

    if (typeof aoCallback === 'function') {
      // Call the given callback
      aoCallback(success, asSKU);
    }

    // Trigger the cart update event
    triggerWishlistItemRemovedEvent(asSKU);
  }).fail(() => {
    if (typeof aoCallback === 'function') {
      // Call the given callback
      aoCallback(false, asSKU);
    }
  });
}

/**
 * Adds one or more items to the cart.
 * @param {Array}  aaProductList          - List of product objects.
 * @param {String} aaProductList[].sku   - SKU for the product.
 * @param {String} aaProductList[].title - Title for the product.
 * @param {String} aaProductList[].size  - Size for the product.
 * @param {String} aaProductList[].price - Price for the product.
 */
function addItems(aaProductList) {
  if (isEmpty(aaProductList) || (type(aaProductList) !== 'array') || !aaProductList.length) {
    return false;
  }

  const skusArray = [];
  const newProductList = [];

  for (let i = 0; i < aaProductList.length; i += 1) {
    const productItem = aaProductList[i];

    if (
      !isEmpty(productItem.sku) &&
      ($.inArray(productItem.sku, skusList) === -1)
    ) {
      // Save new SKUs
      skusArray.push(productItem.sku);
      newProductList.push({
        sku: productItem.sku,
        title: productItem.title,
        size: productItem.size,
        price: productItem.price,
      });
    }
  }

  if (!skusArray.length) {
    return false;
  }

  // Make the AJAX request
  addToWishList(skusArray, newProductList, (abSuccess) => {
    if (abSuccess) {
      // Update the SKUs list
      for (let i = 0; i < skusArray.length; i += 1) {
        skusList.push(skusArray[i]);
      }
    }
  });

  return true;
}

/**
 * Adds one item to the wishlist.
 * @param {Object} aoProductData       - Product data.
 * @param {String} aoProductData.sku   - SKU for the product.
 * @param {String} aoProductData.title - Title for the product.
 * @param {String} aoProductData.size  - Size for the product.
 * @param {String} aoProductData.price - Price for the product.
 */
function addItem(aoProductData) {
  return addItems([aoProductData]);
}

/**
 * Removes an item from the wishlist.
 * @param {String} asSKU - SKU
 */
function removeItem(asSKU) {
  if (isEmpty(asSKU)) {
    return false;
  }

  // Make the AJAX request
  removeFromWishList(asSKU, (abSuccess) => {
    if (abSuccess) {
      // Update the SKUs list
      const index = getItemIndex(asSKU);
      if (index >= 0) {
        skusList.splice(index, 1);
      }
    }
  });

  return true;
}

function getLoginUrlAttr() {
  return loginUrlAttr;
}

function getAddToWishListJSClassName() {
  return jsAddToWishlistClass;
}

function templateURL(dataURL) {
  let url = dataURL;
  const skus = '&skus=';

  url += skus + skusList;

  return url;
}

function initWishlistURL(wishListButton) {
  const wishListBtn = wishListButton;
  const wishListLoginURL = wishListBtn.data('login-url');

  if (wishListLoginURL.length) {
    // If not logged in, handle redirection to login panel
    if (wishListBtn.is('a')) {
      wishListBtn.attr('href', templateURL(wishListLoginURL));
    }

    return true;
  }

  return false;
}

// Named exports - Functions
export {
  getItemsCount,
  hasItems,
  updateSkusList,
  addItem,
  addItems,
  removeItem,
  initWishlistURL,
  getLoginUrlAttr,
  getAddToWishListJSClassName,
};



// WEBPACK FOOTER //
// ./js/common/Wishlist.js